import { Container, Sprite, type Texture } from "pixi.js";
import { CoverBackground, spriteVariant } from "@slot/presentation";
import type { PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap } from "@slot/game-shell";
import type { FeatureFinishedPort, LocalizationService } from "@slot/game-shell";
import { PanelMotion, type PanelMotionTuning } from "../PanelMotion";
import { TEXT, label, value, type FittedLine } from "../textStyles";

/**
 * `FeatureFinishedPanel/simpleRoot` from the scene dump (y-up flipped): the 1534×1064 `ResponsiveContainer` (max width
 * 1534) 65 below the centre, its `AnimatedContainer` 46.3 up, with the title, the total and the subtitle; the cover is
 * the `spriteBackground` prefab (the boot art, `background-intro`, cover-fit).
 */
const GEOMETRY = {
  content: { width: 1534, height: 1064.1, y: 65 },
  limits: { desktop: { maxWidth: 1534 }, mobile: { maxWidth: 1534 }, mobile_landscape: {} },
  panelY: -46.3,
  title: { y: -270.5, fontSize: 103.4, box: { width: 1126.3, height: 240.4 } },
  amount: { y: -84, fontSize: 145.6, box: { width: 934.3, height: 202.6 } },
  subtitle: { y: 115, fontSize: 112.5, box: { width: 958.3, height: 147.3 } },
} as const;

/**
 * The free spins' end behind `FeatureFinishedPort`: over the boot art, "You Have Won / <total> / in Free Spins",
 * dismissed by a tap. The Cocos panel also had an offer layout (buy extra spins), shown for the features in
 * `extraSpinsOfferFeatureIds`; the bloody-teddy backend names none, so it never showed and is not ported (`hasOffer`
 * false: the shell never asks for it).
 */
export class FeatureFinishedView extends Container implements FeatureFinishedPort {
  public readonly hasOffer = false;
  private readonly chrome: ModalChrome;
  private readonly motion: PanelMotion;
  private readonly summary: FittedLine;
  private readonly cover: CoverBackground | null;

  public constructor(input: { cover: Texture | null; viewport: ViewportStore; localization: LocalizationService; runner: PresentationTaskRunner; motion: PanelMotionTuning }) {
    super({ label: "FeatureFinishedPanel" });
    const g = GEOMETRY;
    this.chrome = new ModalChrome({
      label: "simpleRoot",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { width: g.content.width, height: g.content.height, y: g.content.y, limits: g.limits },
    });
    this.addChild(this.chrome);

    if (input.cover) {
      this.cover = new CoverBackground({ variants: { shared: spriteVariant(new Sprite(input.cover)) } });
      this.cover.label = "BackgroundLayer";
      this.cover.bind(input.viewport);
      this.chrome.backdrop.addChild(this.cover);
    } else {
      this.cover = null;
    }

    const panel = new Container({ label: "AnimatedContainer" });
    panel.position.set(0, g.panelY);
    this.summary = value({ style: TEXT.panel, ...g.amount, label: "AmountLabel" });
    panel.addChild(
      label({ localization: input.localization, key: "featuretrigger.title", fallback: "You Have Won", style: TEXT.panel, ...g.title, label: "Title" }),
      this.summary,
      label({ localization: input.localization, key: "featurefinished.subtitle", fallback: "in Free Spins", style: TEXT.panel, ...g.subtitle, label: "SubTitle" }),
    );
    this.chrome.content.addChild(panel);
    this.motion = new PanelMotion({
      chrome: this.chrome,
      panel,
      background: this.cover,
      dim: false,
      runner: input.runner,
      tuning: input.motion,
      loopScope: "feature_finished_idle_pulse",
    });
  }

  public showSimple(): void {
    this.motion.preSeat();
    this.chrome.visible = true;
  }

  public showOffer(): void {
    this.showSimple();
  }

  public hide(): void {
    this.chrome.visible = false;
    this.motion.stop();
  }

  public setSummary(text: string): void {
    this.summary.setLine(text ?? "");
  }

  public setCostText(_text: string): void {}

  public setSkipEnabled(enabled: boolean): void {
    this.chrome.skipButton.visible = enabled;
  }

  public setBuyEnabled(_enabled: boolean): void {}

  public setDeclineEnabled(_enabled: boolean): void {}

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.chrome.skipButton, onPress);
  }

  public bindBuyTap(_onPress: () => void): () => void {
    return () => {};
  }

  public bindDeclineTap(_onPress: () => void): () => void {
    return () => {};
  }

  public playEnterAnimation(): Promise<void> {
    return this.motion.enter();
  }

  public playExitAnimation(): Promise<void> {
    return this.motion.exit();
  }

  public fadeInContinueText(): Promise<void> {
    return this.motion.fadeInContinueText();
  }

  public dispose(): void {
    this.cover?.dispose();
    this.chrome.dispose();
    this.destroy({ children: true });
  }
}
